import React, { useEffect, useState } from 'react';
import * as moment from 'moment';
import ThriveSignatureRequests from 'apis/ThriveSignatureRequest/ThriveSignatureRequest';

// ── Step definitions ──────────────────────────────────────────────────────────
// Logs are ALWAYS matched by `step_name` (never by step_id), because Unlocked / Trial
// logs use a different step_id numbering (Internal Notification is removed).
const STEP_META = {
  request:      { name: 'Request Received',      title: 'Request Received' },
  verification: { name: 'Account Verification',  title: 'Account Verification' },
  internal:     { name: 'Internal Notification', title: 'Internal Notification' },
  assignment:   { name: 'User Assignment',       title: 'User Assignment' },
  welcome:      { name: 'Welcome Notification',  title: 'Welcome Notification' },
  billing:      { name: 'Billing Verification',  title: 'Billing Verification' },
  addon:        { name: 'Add-on Plans',          title: 'Add-on Plans' },
  qualified:    { name: 'Qualified-Sales',       title: 'Qualified Sales Notification' },
};

const CLASSIC_STEPS = ['request', 'verification', 'internal', 'assignment', 'welcome', 'billing', 'addon'];
const UNLOCKED_STEPS = ['request', 'verification', 'assignment', 'welcome', 'addon', 'qualified'];
const TRIAL_STEPS = ['request', 'verification', 'assignment', 'welcome', 'addon'];

const DATE_FORMAT = "ddd, Do MMM 'YY - h:mm A";

const Timeline = ({ requestId, status, plan }) => {
  const [data, setData] = useState([]);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const { getLogs } = ThriveSignatureRequests();

  const isUnlocked = plan === 'Unlocked';
  const isTrail = plan === 'Trial';
  const isClassic = !isUnlocked && !isTrail;
  const isRejected = status == 'Rejected';

  const steps = isUnlocked ? UNLOCKED_STEPS : isTrail ? TRIAL_STEPS : CLASSIC_STEPS;

  useEffect(() => {
    handleGetLogs();
  }, []);

  const handleGetLogs = async () => {
    try {
      const values = {
        requestId: requestId,
        type: plan === 'Unlocked' ? 'Unlock Enrollment' : 'Enrollment',
      };

      const res = await getLogs(values);
      if (res) {
        setData(res.data || []);
        setLogsLoaded(true);
      }
    } catch (e) {
      console.error('Get error:', e);
    }
  };

  // ── Step state derived from logs ────────────────────────────────────────────
  const logsFor = (key) => (data || []).filter(i => i.step_name == STEP_META[key].name);
  const isDone = (key) => logsFor(key).length > 0;

  const firstIncompleteIdx = steps.findIndex(k => !isDone(k));

  // Active (blue) step:
  //  - the first step without logs, and
  //  - Classic only: Add-on Plans is available in parallel with Billing Verification once Welcome is done.
  const isActive = (key, idx) => {
    if (!logsLoaded || isRejected || isDone(key)) return false;
    if (idx === firstIncompleteIdx) return true;
    return isClassic && key === 'addon' && isDone('welcome');
  };

  const getBoxColor = (key, idx) => {
    if (isRejected && key === 'verification') return 'red';
    if (isDone(key)) return '#4CAF50';
    if (isActive(key, idx)) return '#2196F3';
    return '#CFD8DC';
  };

  const styles = {
    body: {
      margin: 0,
      fontFamily: 'Arial, sans-serif',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '80vh',
    },
    timeline: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      margin: '50px auto',
      padding: '10px',
      background: '#fff',
      borderRadius: '50px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      width: 'fit-content',
      gap: '5px',
    },
    boxBase: {
      position: 'relative',
      width: '120px',
      height: '50px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#fff',
      fontWeight: 'bold',
      textAlign: 'center',
      borderRadius: '8px',
      cursor: 'default',
      transition: 'background-color 0.3s ease',
    },
    info: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '200px',
      background: '#fff',
      padding: '12px 14px',
      borderRadius: '10px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      textAlign: 'left',
      fontSize: '14px',
      lineHeight: '1.4',
      boxSizing: 'border-box',
      zIndex: 10,
    },
    infoTop: {
      bottom: '150%',
      marginBottom: '10px',
    },
    infoBottom: {
      top: '150%',
      marginTop: '12px',
    },
    infoBefore: {
      content: '""',
      position: 'absolute',
      left: '50%',
      width: '2px',
      height: '30px',
      background: 'currentColor',
      transform: 'translateX(-50%)',
    },
    heading: {
      margin: '0 0 4px',
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
    },
    incompleteHeading: {
      margin: '0 0 4px',
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      opacity: 0.5,
    },
    timestamp: {
      margin: '0 0 10px',
      fontSize: '12px',
      color: '#888',
      whiteSpace: 'nowrap',
    },
    list: {
      margin: 0,
      paddingLeft: '18px',
      color: '#555',
      fontSize: '13px',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  };

  // ── Popup content per step ──────────────────────────────────────────────────
  const renderLogs = (key, logs) => {
    switch (key) {
      case 'verification':
        return logs.map((d, idx) => (
          <React.Fragment key={idx}>
            <li>{d.notes}</li>
            <p style={styles.timestamp}>{moment(d.createdAt).format(DATE_FORMAT)}</p>
            {isRejected && <li>Reason: {d.value}</li>}
          </React.Fragment>
        ));

      case 'assignment':
        return logs.map((d, idx) => (
          <React.Fragment key={idx}>
            <li>{d.notes}</li>
            <p style={styles.timestamp}>{moment(d.createdAt).format(DATE_FORMAT)}</p>
            {d.start_date && <li><span>Enrollment Start Date :{moment(d.start_date).format('MM/DD/YYYY')}</span></li>}
            {d.loyalty_id && <li><span>Member ID :{d.loyalty_id}</span></li>}
          </React.Fragment>
        ));

      case 'welcome':
        return logs.map((d, idx) => (
          <React.Fragment key={idx}>
            <li>{d.notes}</li>
            {!d.notes?.toLowerCase().includes('not sent') && (
              <p style={styles.timestamp}>{moment(d.createdAt).format(DATE_FORMAT)}</p>
            )}
          </React.Fragment>
        ));

      case 'qualified':
        return logs.map((d, idx) => (
          <React.Fragment key={idx}>
            <li>Qualified Merch Spend: <p style={styles.timestamp}>$500+</p></li>
            <li>Email Status: <p style={styles.timestamp}>Sent</p></li>
            <li>Email Sent Date & Time: <p style={styles.timestamp}>{moment(d.createdAt).format(DATE_FORMAT)}</p></li>
          </React.Fragment>
        ));

      default:
        return logs.map((d, idx) => (
          <React.Fragment key={idx}>
            <li>{d.notes}</li>
            <p style={styles.timestamp}>{moment(d.createdAt).format(DATE_FORMAT)}</p>
          </React.Fragment>
        ));
    }
  };

  return (
    <div style={styles.body}>
      <div style={styles.timeline}>
        {steps.map((key, idx) => {
          const meta = STEP_META[key];
          const logs = logsFor(key);
          const done = logs.length > 0;
          const active = isActive(key, idx);
          const color = getBoxColor(key, idx);

          const isFirst = idx === 0;
          const isLast = idx === steps.length - 1;

          // Popup placement alternates by position: 1st -> above, 2nd -> below, ...
          const top = idx % 2 === 0;
          const infoPos = top ? styles.infoTop : styles.infoBottom;
          const connectorPos = top ? { top: '100%' } : { bottom: '100%' };

          return (
            <div
              key={key}
              style={{
                ...styles.boxBase,
                backgroundColor: color,
                ...(isFirst && { borderTopLeftRadius: '50px', borderBottomLeftRadius: '50px' }),
                ...(isLast && { borderTopRightRadius: '50px', borderBottomRightRadius: '50px' }),
              }}
            >
              {idx + 1}
              <div style={{ ...styles.info, ...infoPos, color }}>
                <div style={{ ...styles.infoBefore, ...connectorPos, backgroundColor: color }} />
                <h3 style={(done || active) ? styles.heading : styles.incompleteHeading}>{meta.title}</h3>
                {done && <ul style={styles.list}>{renderLogs(key, logs)}</ul>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;